import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { useAuth } from '@/hooks/useAuth';
// DEMO-4 — the `mockProjects` fallback ("Summer Campaign 2024",
// "Santorini Wedding", due dates in May–July 2024) is gone, along with
// `getProjectById`. `demoProjects` is the only demo source, and the
// store is hydrated from it.
import { demoProjects } from '@/data/demoOverlay';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';
import { debug } from '@/lib/debug';
import { useToast } from '@/hooks/use-toast';
import { useNovaEmotion } from '@/components/nova/NovaEmotion';
import { track } from '@/lib/analytics';

export type Project = Tables<'projects'>;
export type ProjectInsert = TablesInsert<'projects'>;

/**
 * PLANS-1B — typed error thrown when the org's Starter-tier active
 * project cap (5) is reached. Caught by project-create surfaces to
 * render an inline `UpgradeNudgeCard(reason: 'project_cap')` instead
 * of a red toast.
 *
 * Relies on the `can_add_project(org_id)` RPC added in PLANS-1A. If
 * the RPC returns { allowed: false, current, cap }, we throw this and
 * the insert never happens.
 */
export class ProjectCapReachedError extends Error {
  code = 'project_cap_reached' as const;
  current: number;
  cap: number;
  constructor(current: number, cap: number) {
    super(
      `You've reached your Orbit Starter active project limit (${current} of ${cap}). Upgrade to Orbit for unlimited projects.`,
    );
    this.name = 'ProjectCapReachedError';
    this.current = current;
    this.cap = cap;
  }
}

export const useProjects = () => {
  const { isDemo } = useDemoMode();
  const { user } = useAuth();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['projects', user?.id, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (isDemo) {
        // Session-scoped store first so newly-created Preview Studio
        // projects show up in the list; falls back to the seed if the
        // store hasn't been hydrated (e.g. during SSR / tests).
        const rows = demoStore.projects.list();
        return (rows.length > 0 ? rows : demoProjects) as any;
      }

      // First get user's org_id from profile
      if (!user) return [];
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .single();
      
      if (!profile?.org_id) return [];
      
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          client:clients(*),
          milestones(*)
        `)
        .eq('org_id', profile.org_id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user || isDemo,
  });
};

export const useProject = (projectId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const { user } = useAuth();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['project', projectId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (!projectId) return null;

      if (isDemo) {
        // Look up in the mutable store first so recently-edited rows
        // reflect their new values; fall back to seed + legacy mock.
        const rows = demoStore.projects.list();
        const fromStore = rows.find((p) => p.id === projectId);
        if (fromStore) return fromStore as any;
        const fromSeed = demoProjects.find((p) => p.id === projectId);
        if (fromSeed) return fromSeed as any;
        // DEMO-4 — no 2024-mock fallback. An id that resolves nowhere
        // in Preview Studio is genuinely gone (the store resets on
        // refresh), and ProjectWorkspace says exactly that.
        return null;
      }
      
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          client:clients(*),
          milestones(*)
        `)
        .eq('id', projectId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: (!!user || isDemo) && !!projectId,
  });
};

export const useClient = (clientId: string | undefined) => {
  const { isDemo } = useDemoMode();

  return useQuery({
    queryKey: ['client', clientId, isDemo],
    queryFn: async () => {
      if (!clientId) return null;
      
      if (isDemo) {
        // For demo mode, return null as clients are embedded in projects
        return null;
      }
      
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!clientId,
  });
};

// ────────────────────────────────────────────────────────────────
// PORTAL-1 — Public project portal payload + hook.
//
// Bundles project + client + branding + deliverables + files +
// milestones + galleries + watermark + timeline events into ONE
// anon-safe RPC call. Mirrors the GAL-2 `usePublicGalleryById`
// pattern. The old `useProjectByToken` / `useDeliverablesByToken`
// / etc. hooks stay in place so the studio-side ClientPortal
// preview and existing test surfaces don't break — they just
// aren't the primary path anymore.
// ────────────────────────────────────────────────────────────────

export interface PublicProjectPortalPayload {
  project: {
    id: string;
    name: string;
    description: string | null;
    status: string;
    stage: string | null;
    due_date: string | null;
    scheduled_date: string | null;
    location: string | null;
    portal_enabled: boolean;
    created_at: string;
    updated_at: string;
  };
  client: {
    id: string;
    name: string;
    brand: string | null;
    logo_url: string | null;
    email: string | null;
  } | null;
  branding: {
    studio_name: string;
    /** Legacy BRAND-1 mark — COALESCE(brand_logo_url, logo_url). */
    logo_url: string | null;
    /** BRAND-2 dark-surface variant (raw column; may be absent on
     *  environments that haven't run 20260731100000 yet). */
    logo_dark_url?: string | null;
    /** BRAND-2 light-surface variant (raw column; may be absent on
     *  environments that haven't run 20260731100000 yet). */
    logo_light_url?: string | null;
    brand_color: string;
    website: string | null;
    contact_email: string | null;
  } | null;
  deliverables: Array<{
    id: string;
    file_url: string | null;
    thumbnail_url: string | null;
    type: string;
    version: string | null;
    approved: boolean;
    feedback: string | null;
    created_at: string;
    updated_at: string;
  }>;
  files: Array<{
    id: string;
    name: string;
    file_url: string;
    category: string;
    created_at: string;
  }>;
  milestones: Array<{
    id: string;
    name: string;
    completed: boolean;
    due_date: string | null;
    sort_order: number;
  }>;
  galleries: Array<{
    id: string;
    name: string;
    description: string | null;
    is_public: boolean;
    download_enabled: boolean;
    proofing_enabled: boolean;
    cover_photo_id: string | null;
    cover_url: string | null;
    photo_count: number;
    created_at: string;
  }>;
  watermark: {
    image_url: string | null;
    text: string | null;
    position: string;
    opacity: number;
    scale: number;
  } | null;
  timeline_events: {
    project_opened: string | null;
    shoot_date: string | null;
    delivery_due: string | null;
    first_delivery: string | null;
  };
}

/**
 * usePublicProjectPortal — the primary fetch for `/portal/:token`.
 *
 * Returns `null` (rather than throwing) when the portal is disabled
 * or the token doesn't exist, so the page can render a friendly
 * "not found" state without a red toast.
 *
 * Photos rarely change mid-view; deliverables + comments come in
 * over their own realtime channels, so a 60s staleTime is fine.
 */
export const usePublicProjectPortal = (token: string | undefined) => {
  return useQuery({
    queryKey: ['public-project-portal', token],
    queryFn: async (): Promise<PublicProjectPortalPayload | null> => {
      if (!token) return null;
      const { data, error } = await (supabase as any).rpc(
        'get_public_project_portal',
        { p_token: token },
      );
      if (error) throw error;
      return (data ?? null) as PublicProjectPortalPayload | null;
    },
    enabled: !!token,
    staleTime: 60_000,
  });
};

export const useProjectByToken = (token: string | undefined) => {
  return useQuery({
    queryKey: ['project-by-token', token],
    queryFn: async () => {
      if (!token) return null;
      
      // Only select fields that should be visible through the portal
      // Excludes: budget, notes, internal data
      const { data, error } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          description,
          status,
          stage,
          due_date,
          portal_enabled,
          portal_token,
          created_at,
          updated_at,
          client:clients(id, name, brand, logo_url),
          milestones(id, name, completed, due_date, sort_order, project_id)
        `)
        .eq('portal_token', token)
        .eq('portal_enabled', true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!token,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async (project: ProjectInsert) => {
      if (isDemo) {
        const now = new Date().toISOString();
        const created = {
          id: `demo-p-${crypto.randomUUID().slice(0, 8)}`,
          created_at: now,
          updated_at: now,
          status: (project as any).status ?? 'discovery',
          stage: (project as any).stage ?? (project as any).status ?? 'discovery',
          milestones: [],
          ...project,
        };
        return demoStore.projects.add(created) as unknown as Project;
      }

      debug.log('Creating project', { name: project.name, orgId: project.org_id });

      // PLANS-1B — Starter caps at 5 active projects. Consult the
      // `can_add_project()` RPC before hitting the insert; on
      // disallowed, throw a typed error so create-project surfaces can
      // swap in an inline UpgradeNudgeCard instead of a destructive
      // toast.
      //
      // FIX-8 — the RPC signature is `can_add_project(p_org_id uuid)`
      // (migration 20260729230600); we passed `{ org_id }` behind an
      // `as any`, PostgREST rejected every call, and the catch-all
      // swallowed the error — so the cap was NEVER enforced. The guard
      // now FAILS CLOSED: any gate error blocks the insert with a loud
      // toast (via the mutation's onError) instead of skipping the
      // check.
      if (project.org_id) {
        const gate = await supabase.rpc('can_add_project', {
          p_org_id: project.org_id,
        });
        if (gate.error) {
          debug.error(
            { action: 'can_add_project', payload: { org_id: project.org_id } },
            gate.error,
          );
          throw new Error(
            "Couldn't verify your plan's project limit, so nothing was created. Try again in a moment.",
          );
        }
        const payload = gate.data as unknown as
          | { allowed?: boolean; current?: number; cap?: number }
          | null;
        if (payload && payload.allowed === false) {
          const current = payload.current ?? 0;
          const cap = payload.cap ?? 5;
          track('project_limit_reached', {
            surface: 'projects_create',
            current,
            cap,
          });
          throw new ProjectCapReachedError(current, cap);
        }
      }

      const { data, error } = await supabase
        .from('projects')
        .insert(project)
        .select()
        .single();

      if (error) {
        debug.error({ action: 'create_project', payload: project }, error);
        throw error;
      }
      debug.success('Project created', { id: data.id });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error: Error) => {
      // PLANS-1B — swallow cap errors so the destructive toast doesn't
      // fire on top of the caller's inline UpgradeNudgeCard.
      if (error instanceof ProjectCapReachedError) return;
      toast({
        title: 'Error creating project',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, syncToInquiry = true, ...updates }: Partial<Project> & { id: string; syncToInquiry?: boolean }) => {
      if (isDemo) {
        const merged = demoStore.projects.update(id, updates as any);
        return (merged ?? { id, ...updates }) as unknown as Project;
      }

      debug.log('Updating project', { id, fields: Object.keys(updates) });
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .select('*, inquiry_id')
        .single();
      
      if (error) {
        debug.error({ action: 'update_project', payload: { id, ...updates } }, error);
        throw error;
      }

      // Sync stage/status to linked inquiry if it exists
      if (syncToInquiry && data.inquiry_id && updates.stage) {
        const statusMapping: Record<string, string> = {
          discovery: 'pending',
          pre_production: 'accepted',
          production: 'in_progress',
          post_production: 'in_progress',
          delivered: 'completed',
        };

        const inquiryUpdates: Record<string, unknown> = {
          stage: updates.stage,
        };
        
        if (updates.status && statusMapping[updates.status]) {
          inquiryUpdates.status = statusMapping[updates.status];
        }

        await supabase
          .from('inquiries')
          .update(inquiryUpdates)
          .eq('id', data.inquiry_id);
      }

      debug.success('Project updated', { id });
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', data.id] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating project',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

export const useCreateMilestones = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (milestones: TablesInsert<'milestones'>[]) => {
      const { data, error } = await supabase
        .from('milestones')
        .insert(milestones)
        .select();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

/**
 * Subscribe to row-level changes on the `projects` table and invalidate
 * the projects cache so changes from any device propagate everywhere.
 *
 * Triggers Nova reactions on key transitions:
 *   - new project inserted → has-suggestion (a soft "ooh, new thing")
 *   - status flipped to 'delivered' → celebrating (the big moment)
 */
export const useProjectsRealtime = () => {
  const queryClient = useQueryClient();
  const { trigger: triggerNova } = useNovaEmotion();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('projects-live-web')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'projects' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          triggerNova('has-suggestion', { duration: 2500 });
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects' },
        (payload: any) => {
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          if (payload?.new?.status === 'delivered' && payload?.old?.status !== 'delivered') {
            triggerNova('celebrating', { duration: 4000 });
          }
        },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'projects' },
        () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, triggerNova]);
};
